import React from 'react';
import { X, Upload, FileText, CheckCircle, AlertCircle, Loader, ArrowLeft, ArrowRight } from 'lucide-react';
import { auth } from '../../lib/firebase';
import { notifyToast } from '../../lib/toastStore';
import Modal from '../shared/Modal';
import SearchableSelect from '../shared/SearchableSelect';

/* ── Field definitions for mapping ── */
const APP_FIELDS = [
  { id: 'firstName', label: 'First Name *', required: true, category: 'Personal' },
  { id: 'lastName', label: 'Last Name *', required: true, category: 'Personal' },
  { id: 'email', label: 'Email *', required: true, category: 'Contact' },
  { id: 'phone', label: 'Phone', category: 'Contact' },
  { id: 'country', label: 'Country', category: 'Personal' },
  { id: 'gender', label: 'Gender', category: 'Personal' },
  { id: 'dateOfBirth', label: 'Date of Birth', category: 'Personal' },
  { id: 'programType', label: 'Program Type', category: 'Program' },
  { id: 'programId', label: 'Program ID', category: 'Program' },
  { id: 'programName', label: 'Program Name', category: 'Program' },
  { id: 'status', label: 'Application Status', category: 'Application' },
  { id: 'placementCompany', label: 'Placement Company', category: 'Placement' },
  { id: 'placementCity', label: 'Placement City', category: 'Placement' },
  { id: 'placementState', label: 'Placement State', category: 'Placement' },
  { id: 'emergencyContactName', label: 'Emergency Contact Name', category: 'Emergency' },
  { id: 'emergencyContactPhone', label: 'Emergency Contact Phone', category: 'Emergency' },
  { id: 'emergencyContactRelationship', label: 'Emergency Contact Relationship', category: 'Emergency' },
  { id: '_skip', label: '— Skip Column —', category: '' },
];

const CATEGORIES = ['Personal', 'Contact', 'Program', 'Placement', 'Application', 'Emergency'];
const APP_FIELDS_BY_CAT = CATEGORIES.reduce((acc, cat) => {
  acc[cat] = APP_FIELDS.filter(f => f.category === cat);
  return acc;
}, {} as Record<string, typeof APP_FIELDS>);

/* ── Simple CSV Parser ── */
function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let current = '';
  let inQuotes = false;
  const row: string[] = [];
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < text.length && text[i + 1] === '"') { current += '"'; i++; }
        else { inQuotes = false; }
      } else { current += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ',') { row.push(current.trim()); current = ''; }
      else if (ch === '\n' || (ch === '\r' && text[i + 1] === '\n')) {
        row.push(current.trim()); current = '';
        if (row.length > 0 && row.some(c => c)) rows.push([...row]);
        while (row.length) row.pop();
      } else if (ch === '\r') {
        row.push(current.trim()); current = '';
        if (row.length > 0 && row.some(c => c)) rows.push([...row]);
        while (row.length) row.pop();
      } else { current += ch; }
    }
  }
  if (current.trim() || row.length > 0) {
    row.push(current.trim());
    if (row.some(c => c)) rows.push([...row]);
  }
  return rows;
}

interface CsvImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  programs?: { id: string; name: string; programId?: string }[];
}

export default function CsvImportModal({ isOpen, onClose, onImportComplete, programs }: CsvImportModalProps) {
  const [step, setStep] = React.useState(1);
  const [rawText, setRawText] = React.useState('');
  const [headers, setHeaders] = React.useState<string[]>([]);
  const [previewRows, setPreviewRows] = React.useState<string[][]>([]);
  const [allRows, setAllRows] = React.useState<string[][]>([]);
  const [mapping, setMapping] = React.useState<Record<number, string>>({});
  const [importing, setImporting] = React.useState(false);
  const [results, setResults] = React.useState<{ row: number; status: string; applicationId?: string; error?: string }[] | null>(null);
  const [duplicateEmails, setDuplicateEmails] = React.useState<Set<string>>(new Set());
  const [selectedProgram, setSelectedProgram] = React.useState('');
  const [ignoreDuplicates, setIgnoreDuplicates] = React.useState(false);
  const [skipExistingProgramApps, setSkipExistingProgramApps] = React.useState(false);

  const reset = () => {
    setStep(1); setRawText(''); setHeaders([]); setPreviewRows([]); setAllRows([]);
    setMapping({}); setImporting(false); setResults(null); setDuplicateEmails(new Set());
    setSelectedProgram(''); setIgnoreDuplicates(false); setSkipExistingProgramApps(false);
  };

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setRawText(text);
      const parsed = parseCSV(text);
      if (parsed.length < 2) { notifyToast('warning', 'CSV must have a header row and at least one data row.'); return; }
      const hdrs = parsed[0];
      const data = parsed.slice(1);
      setHeaders(hdrs); setAllRows(data); setPreviewRows(data.slice(0, 5));
      const auto: Record<number, string> = {};
      hdrs.forEach((h, i) => {
        const key = h.toLowerCase().replace(/[^a-z0-9]/g, '');
        const match = APP_FIELDS.find(f => f.id !== '_skip' && (f.id.toLowerCase() === key || f.label.toLowerCase().replace(/[^a-z0-9]/g, '') === key || key.includes(f.id.toLowerCase())));
        if (match) auto[i] = match.id;
      });
      setMapping(auto);
      const emails = data.map(row => { const emailIdx = hdrs.findIndex(h => h.toLowerCase().includes('email')); return emailIdx >= 0 ? row[emailIdx]?.trim().toLowerCase() : ''; }).filter(Boolean);
      const dupes = new Set<string>(); const seen = new Set<string>();
      emails.forEach(e => { if (seen.has(e)) dupes.add(e); seen.add(e); });
      setDuplicateEmails(dupes);
      setStep(2);
    };
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.csv') || file.type === 'text/csv')) handleFile(file);
  };

  const requiredFieldsMapped = () => {
    const mapped = Object.values(mapping);
    return APP_FIELDS.filter(f => f.required).every(f => mapped.includes(f.id));
  };

  const validatedRows = React.useMemo(() => {
    if (allRows.length === 0) return [];
    return allRows.map((row, idx) => {
      const firstNameIdx = headers.findIndex((_, i) => mapping[i] === 'firstName');
      const lastNameIdx = headers.findIndex((_, i) => mapping[i] === 'lastName');
      const emailIdx = headers.findIndex((_, i) => mapping[i] === 'email');
      const firstName = firstNameIdx >= 0 ? row[firstNameIdx]?.trim() : '';
      const lastName = lastNameIdx >= 0 ? row[lastNameIdx]?.trim() : '';
      const email = emailIdx >= 0 ? row[emailIdx]?.trim().toLowerCase() : '';
      const issues: string[] = [];
      if (!firstName) issues.push('Missing first name');
      if (!lastName) issues.push('Missing last name');
      if (!email) issues.push('Missing email');
      else if (!email.includes('@')) issues.push('Invalid email format');
      else if (!ignoreDuplicates && duplicateEmails.has(email)) issues.push('Duplicate email in CSV');
      return { row: idx + 1, firstName, lastName, email, valid: issues.length === 0, issues };
    });
  }, [allRows, mapping, headers, duplicateEmails, ignoreDuplicates]);

  const validCount = validatedRows.filter(r => r.valid).length;
  const invalidCount = validatedRows.filter(r => !r.valid).length;

  const handleImport = async () => {
    setImporting(true);
    try {
      const valid = validatedRows.filter(r => r.valid);
      const items = valid.map(r => {
        const rowData: Record<string, string> = {};
        headers.forEach((_, colIdx) => {
          const fieldId = mapping[colIdx];
          if (fieldId && fieldId !== '_skip' && allRows[r.row - 1]) {
            rowData[fieldId] = allRows[r.row - 1][colIdx]?.trim() || '';
          }
        });
        return {
          itemType: 'applicant_row',
          inputSnapshot: { rowNum: r.row, rowData },
        };
      });

      if (!selectedProgram) { notifyToast('warning', 'Please select a program before importing.'); setImporting(false); return; }
      const selectedProgramObj = (programs || []).find((p: any) => p.id === selectedProgram || p.programId === selectedProgram);
      const resolvedProgramId = selectedProgramObj?.id || selectedProgram;
      const token = await auth.currentUser?.getIdToken();

      const res = await fetch('/api/admin/bulk-jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          type: 'bulk_applicant_import',
          items,
          options: {
            programId: resolvedProgramId,
            allowOverwrite: !skipExistingProgramApps,
          },
        }),
      });
      const data = await res.json();

      if (data.success && data.jobId) {
        reset();
        onClose();
        notifyToast('success', 'Applicant Import Task Queued! Tracking progress in the Task Tracker.');
        window.dispatchEvent(new CustomEvent('bulk-job-created'));
      } else {
        notifyToast('error', 'Failed to enqueue job: ' + (data.error || 'Unknown error'));
      }
    } catch (err: any) { notifyToast('error', 'Import failed: ' + err.message); }
    setImporting(false);
  };

  if (!isOpen) return null;

  return (
    <Modal onClose={() => { reset(); onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-brand" />
            <h2 className="text-sm font-extrabold text-slate-900">Import Applicants from CSV</h2>
          </div>
          <button aria-label="Close" title="Close" onClick={() => { reset(); onClose(); }} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Steps Indicator */}
        <div className="flex items-center gap-2 px-6 py-3 bg-slate-50 border-b border-slate-200 text-caption font-bold">
          {[{ n: 1, label: 'Upload' }, { n: 2, label: 'Map Fields' }, { n: 3, label: 'Review' }, { n: 4, label: 'Results' }].map((s, i) => (
            <React.Fragment key={s.n}>
              {i > 0 && <div className="h-px flex-1 bg-slate-300" />}
              <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full ${step === s.n ? 'btn-primary text-white' : step > s.n ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>
                {step > s.n ? <CheckCircle className="h-3 w-3" /> : <span>{s.n}</span>}
                <span>{s.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* STEP 1: Upload */}
          {step === 1 && (
            <div className="space-y-5">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                <label className="text-xs font-extrabold text-slate-900 flex items-center"><span className="text-rose-500 mr-1">*</span>Select Program</label>
                <SearchableSelect
                  value={selectedProgram}
                  onChange={setSelectedProgram}
                  options={(programs || []).filter((p: any) => p.active !== false).map((p: any) => ({ value: p.id, label: p.name }))}
                  placeholder="Search programs…"
                  clearLabel="-- Choose a program to assign all imported applicants --"
                  emptyLabel="No programs found"
                />
                {!selectedProgram && rawText && <p className="text-caption font-bold text-amber-600">Please select a program before proceeding.</p>}
              </div>
              <div onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} className="border-2 border-dashed border-slate-300 rounded-xl p-12 text-center  transition-colors cursor-pointer" onClick={() => {
                if (!selectedProgram) { notifyToast('warning', 'Please select a program first.'); return; }
                const input = document.createElement('input'); input.type = 'file'; input.accept = '.csv,text/csv';
                input.onchange = (e: any) => { if (e.target.files[0]) handleFile(e.target.files[0]); }; input.click();
              }}>
                <Upload className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm font-bold text-slate-700">Drop a CSV file here or click to browse</p>
                <p className="text-caption text-slate-400 mt-1">The CSV should have a header row with column names</p>
              </div>
            </div>
          )}

          {/* STEP 2: Field Mapping */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-extrabold text-slate-800">Map CSV Columns to Application Fields</h3>
                  <p className="text-caption text-slate-400 mt-0.5">Select which application field each CSV column corresponds to. Fields marked * are required.</p>
                </div>
                <button onClick={() => setStep(3)} disabled={!requiredFieldsMapped()} className="px-4 py-2 btn-primary  disabled:bg-slate-300 text-white text-xs font-bold rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed flex items-center gap-1.5">
                  <span>Continue</span><ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
              {(() => { const c = Object.keys(mapping).length; return <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-xs text-brand font-bold">✓ Auto-mapped {c} of {headers.length} columns — review and adjust below</div>; })()}
              <div className="overflow-x-auto border border-slate-200 rounded-xl">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-3 py-2 text-caption font-bold text-slate-400 uppercase">CSV Column Header</th>
                      <th className="px-3 py-2 text-caption font-bold text-slate-400 uppercase w-56">Maps To</th>
                      {previewRows.slice(0, 4).map((_, ri) => (<th key={ri} className="px-3 py-2 text-caption font-bold text-slate-400 uppercase">Row {ri + 2}</th>))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {headers.map((hdr, ci) => {
                      const mapped = mapping[ci] || '';
                      const field = APP_FIELDS.find(f => f.id === mapped);
                      const isRequired = field?.required;
                      const hdrClean = hdr.toLowerCase().replace(/[^a-z0-9]/g, '');
                      const headerIsRequired = APP_FIELDS.some(f => f.required && (f.id.toLowerCase() === hdrClean || f.label.toLowerCase().replace(/[^a-z0-9]/g, '') === hdrClean || hdrClean.includes(f.id.toLowerCase())));
                      const catFields = Object.entries(APP_FIELDS_BY_CAT);
                      return (
                        <tr key={ci} className={`${!mapped && APP_FIELDS.find(f => f.required) ? 'border-l-2 border-red-400' : ''} ${!mapped ? 'bg-amber-50/30' : ''} hover:bg-slate-50/50`}>
                          <td className="px-3 py-2.5 font-bold text-slate-700">
                            {hdr}
                            {headerIsRequired && !mapped && <span className="ml-1.5 text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-bold">Required</span>}
                            {mapped && mapped !== '_skip' && <span className="ml-1.5 text-[10px] bg-emerald-100 text-emerald-600 px-1.5 py-0.5 rounded font-bold">✓</span>}
                          </td>
                          <td className="px-3 py-2.5">
                            <select value={mapped} onChange={(e) => setMapping(prev => ({ ...prev, [ci]: e.target.value }))} className={`w-full bg-white border rounded-lg px-2 py-1.5 text-xs outline-none  ${isRequired ? 'border-blue-300 font-bold' : mapped ? 'border-slate-200' : 'border-amber-300'}`}>
                              <option value="">— Not mapped —</option>
                              {catFields.map(([cat, fields]) => (<optgroup key={cat} label={cat}>{fields.map(f => <option key={f.id} value={f.id}>{f.label}{f.required ? ' *' : ''}</option>)}</optgroup>))}
                            </select>
                            {mapped === '_skip' && <span className="text-caption text-slate-400 mt-0.5 block">Column will be ignored</span>}
                          </td>
                          {previewRows.slice(0, 4).map((row, ri) => (<td key={ri} className="px-3 py-2.5 text-slate-600 font-mono max-w-[160px] truncate" title={row[ci]}>{row[ci] || <span className="text-slate-300 italic">empty</span>}</td>))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {!requiredFieldsMapped() && <p className="text-xs text-amber-600 font-bold flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" />Map all required fields (First Name *, Last Name *, Email *) before continuing</p>}
            </div>
          )}

          {/* STEP 3: Review */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-extrabold text-slate-800">Review & Confirm Import</h3>
                  <p className="text-caption text-slate-400 mt-0.5">{allRows.length} rows parsed — {validCount} valid, {invalidCount} with issues</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setStep(2)} className="px-3 py-2 border border-slate-200 text-xs font-bold rounded-xl text-slate-600 hover:bg-slate-50 transition-all cursor-pointer flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
                  <button onClick={handleImport} disabled={importing || validCount === 0 || !selectedProgram} className="px-5 py-2 btn-primary  disabled:bg-slate-300 text-white text-xs font-bold rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed flex items-center gap-1.5">
                    {importing ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    <span>{importing ? `Importing ${validCount} applicants...` : `Import ${validCount} Applicants`}</span>
                  </button>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                <label className="text-xs font-extrabold text-slate-900 flex items-center"><span className="text-rose-500 mr-1">*</span>Program Assignment</label>
                <p className="text-caption text-slate-500">All imported applicants will be assigned to the selected program.</p>
                <SearchableSelect
                  value={selectedProgram}
                  onChange={setSelectedProgram}
                  options={(programs || []).filter((p: any) => p.active !== false).map((p: any) => ({ value: p.id, label: p.name }))}
                  placeholder="Search programs…"
                  clearLabel="-- Select a program (required) --"
                  emptyLabel="No programs found"
                />
                {!selectedProgram && <p className="text-caption font-bold text-rose-500">You must select a program before confirming the import.</p>}
              </div>
              {selectedProgram && (
                <div className="bg-white border border-teal-200 rounded-xl p-4 space-y-3 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center shrink-0 mt-0.5"><CheckCircle className="h-4 w-4 text-teal-600" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-slate-800">Based on the applicants in your file, would you like to skip applicants who already exist in the system for the same program?</p>
                      <p className="text-caption text-slate-500 mt-1">When enabled, any row whose email address already has an application for the selected program will be automatically excluded from the import. No existing data will be overwritten.</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5">
                      <input type="checkbox" checked={skipExistingProgramApps} onChange={(e) => setSkipExistingProgramApps(e.target.checked)} className="sr-only peer" />
                      <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brand-primary rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600"></div>
                      <span className="ms-2 text-xs font-bold text-slate-700">{skipExistingProgramApps ? 'Yes' : 'No'}</span>
                    </label>
                  </div>
                  {duplicateEmails.size > 0 && (
                    <div className="flex items-center gap-2 text-caption text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                      <span>{duplicateEmails.size} email(s) appear multiple times in this file. <label className="underline cursor-pointer font-bold" onClick={() => setIgnoreDuplicates(!ignoreDuplicates)}>Click to {ignoreDuplicates ? 'flag' : 'ignore'} CSV-internal duplicates</label>.</span>
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center"><p className="text-2xl font-extrabold text-slate-800">{allRows.length}</p><p className="text-caption text-slate-500 font-bold">Total Rows</p></div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center"><p className="text-2xl font-extrabold text-emerald-700">{validCount}</p><p className="text-caption text-emerald-600 font-bold">Ready to Import</p></div>
                <div className={`${invalidCount > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50 border border-slate-200'} rounded-xl p-4 text-center`}><p className={`text-2xl font-extrabold ${invalidCount > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{invalidCount}</p><p className={`text-caption font-bold ${invalidCount > 0 ? 'text-amber-600' : 'text-slate-400'}`}>Skipped</p></div>
              </div>
              {invalidCount > 0 && (
                <div className="border border-amber-200 rounded-xl overflow-hidden">
                  <div className="bg-amber-50 px-4 py-2 border-b border-amber-200 flex items-center gap-1.5"><AlertCircle className="h-3.5 w-3.5 text-amber-600" /><span className="text-xs font-bold text-amber-700">Rows with issues (will be skipped)</span></div>
                  <div className="overflow-x-auto max-h-40 overflow-y-auto">
                    <table className="w-full text-left text-xs"><thead className="bg-amber-50/50"><tr className="text-caption font-bold text-slate-500"><th className="px-3 py-1.5">Row</th><th className="px-3 py-1.5">Name</th><th className="px-3 py-1.5">Email</th><th className="px-3 py-1.5">Issue</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">{validatedRows.filter(r => !r.valid).map(r => (<tr key={r.row} className="text-slate-600"><td className="px-3 py-1.5 font-mono">{r.row}</td><td className="px-3 py-1.5">{r.firstName} {r.lastName}</td><td className="px-3 py-1.5 font-mono text-caption">{r.email}</td><td className="px-3 py-1.5 text-amber-600">{r.issues.join(', ')}</td></tr>))}</tbody></table>
                  </div>
                </div>
              )}
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="bg-slate-50 px-4 py-2 border-b border-slate-200"><span className="text-xs font-bold text-slate-700">Valid rows ready for import</span></div>
                <div className="overflow-x-auto max-h-40 overflow-y-auto">
                  <table className="w-full text-left text-xs"><thead className="bg-slate-50/50"><tr className="text-caption font-bold text-slate-400 uppercase"><th className="px-3 py-1.5">#</th><th className="px-3 py-1.5">First Name</th><th className="px-3 py-1.5">Last Name</th><th className="px-3 py-1.5">Email</th></tr></thead>
                  <tbody className="divide-y divide-slate-100">{validatedRows.filter(r => r.valid).slice(0, 50).map(r => (<tr key={r.row} className="text-slate-600"><td className="px-3 py-1.5 font-mono">{r.row}</td><td className="px-3 py-1.5 font-bold text-slate-800">{r.firstName}</td><td className="px-3 py-1.5">{r.lastName}</td><td className="px-3 py-1.5 font-mono text-caption">{r.email}</td></tr>))}</tbody></table>
                </div>
                {validCount > 50 && <p className="px-3 py-1.5 text-caption text-slate-400 italic">...and {validCount - 50} more</p>}
              </div>
            </div>
          )}

          {/* STEP 4: Results */}
          {step === 4 && results && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-extrabold text-slate-800">Import Complete</h3>
                <button onClick={() => { reset(); onImportComplete(); onClose(); }} className="px-4 py-2 btn-primary  text-white text-xs font-bold rounded-xl transition-all cursor-pointer">Done</button>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center"><p className="text-2xl font-extrabold text-slate-800">{results.length}</p><p className="text-caption text-slate-500 font-bold">Total</p></div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center"><p className="text-2xl font-extrabold text-emerald-700">{results.filter(r => r.status === 'imported').length}</p><p className="text-caption text-emerald-600 font-bold">Imported</p></div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center"><p className="text-2xl font-extrabold text-amber-700">{results.filter(r => r.status === 'skipped').length}</p><p className="text-caption text-amber-600 font-bold">Skipped</p></div>
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-center"><p className="text-2xl font-extrabold text-rose-700">{results.filter(r => r.status === 'failed').length}</p><p className="text-caption text-rose-600 font-bold">Failed</p></div>
              </div>
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="bg-slate-50 px-4 py-2 border-b border-slate-200"><span className="text-xs font-bold text-slate-700">Row-by-Row Results</span></div>
                <div className="overflow-x-auto max-h-60 overflow-y-auto">
                  <table className="w-full text-left text-xs"><thead className="bg-slate-50/50"><tr className="text-caption font-bold text-slate-400 uppercase"><th className="px-3 py-1.5">Row</th><th className="px-3 py-1.5">Status</th><th className="px-3 py-1.5">Application ID</th><th className="px-3 py-1.5">Error</th></tr></thead>
                  <tbody className="divide-y divide-slate-100">{results.map(r => (<tr key={r.row} className={`${r.status === 'imported' ? 'text-slate-600' : r.status === 'skipped' ? 'text-amber-600' : 'text-rose-600'}`}><td className="px-3 py-1.5 font-mono">{r.row}</td><td className="px-3 py-1.5 font-bold flex items-center gap-1">{r.status === 'imported' ? <CheckCircle className="h-3 w-3 text-emerald-500" /> : <AlertCircle className="h-3 w-3 text-amber-500" />}<span className="capitalize">{r.status}</span></td><td className="px-3 py-1.5 font-mono">{r.applicationId || '—'}</td><td className="px-3 py-1.5">{r.error || '—'}</td></tr>))}</tbody></table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-slate-200 shrink-0 bg-slate-50">
          <span className="text-caption text-slate-400">
            {step === 1 && 'Upload a CSV file to bulk-import applicants'}
            {step === 2 && `${headers.length} columns detected`}
            {step === 3 && `${validCount} valid / ${invalidCount} invalid`}
            {step === 4 && `${results?.filter(r => r.status === 'imported').length || 0} imported`}
          </span>
          <div className="flex items-center gap-2">
            {step < 4 && <button onClick={() => { reset(); onClose(); }} className="px-3 py-1.5 border border-slate-200 text-xs font-bold rounded-lg text-slate-500 hover:bg-slate-100 transition-all cursor-pointer">Cancel</button>}
            {step === 4 && <button onClick={() => { reset(); onImportComplete(); onClose(); }} className="px-4 py-2 btn-primary  text-white text-xs font-bold rounded-xl transition-all cursor-pointer">Done</button>}
          </div>
        </div>
      </div>
    </Modal>
  );
}
